import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Flex, Text, Button, Table, Badge, Dialog, Select, TextField, Heading, AlertDialog } from '@radix-ui/themes';
import { PlusIcon, ReloadIcon, TrashIcon, CameraIcon } from '@radix-ui/react-icons';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import ClinicLayout from './ClinicLayout';
import { clinicTellihealthApi } from '../../services/api';
import { useOrganization } from '../../contexts/OrganizationContext';
import { isValidDeviceId, deviceIdMessage, deviceIdLabel, DEVICE_MANUFACTURERS, SMARTMETER_MANUFACTURER } from '../../utils/imei';

interface Device {
  device_id: string;
  organization_id: string | null;
  organization_name: string | null;
  serial_number: string | null;
  device_type: string | null;
  manufacturer: string | null;
  active: boolean;
  gen_ver: string | null;
  patient_id: string | null;
  patient_name: string | null;
  linked_at: string | null;
}

interface Organization {
  id: string;
  name: string;
}


const ClinicDevicesPage: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  // PHA-527: org-state from OrganizationContext.
  const { orgId: ctxOrgId, loading: orgLoading } = useOrganization();
  const sessionOrgId: string | null = ctxOrgId || null;
  const sessionLoaded = !orgLoading;

  const [changingOrgDeviceId, setChangingOrgDeviceId] = useState<string | null>(null);

  // Form state
  const [formDeviceId, setFormDeviceId] = useState('');
  const [formOrgId, setFormOrgId] = useState('__none__');
  const [formSerial, setFormSerial] = useState('');
  const [formDeviceType, setFormDeviceType] = useState('');
  const [formManufacturer, setFormManufacturer] = useState('Telli-Health');
  const [submitting, setSubmitting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Device | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // Scan with Phone state
  const [scanMode, setScanMode] = useState(false);
  const [scanToken, setScanToken] = useState<string | null>(null);
  const [scanQrDataUrl, setScanQrDataUrl] = useState<string | null>(null);
  const [scanExpiresAt, setScanExpiresAt] = useState<Date | null>(null);
  const [scanTimeLeft, setScanTimeLeft] = useState('');
  const scanPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scanCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const filterArg = !sessionOrgId || sessionOrgId === 'ALL' ? undefined : sessionOrgId;
      const data = await clinicTellihealthApi.listDevices(filterArg);
      setDevices(data.devices || []);
    } catch {
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [sessionOrgId]);

  const loadOrganizations = useCallback(async () => {
    try {
      const data = await clinicTellihealthApi.getOrganizations();
      const orgs = data.organizations || data.records || data || [];
      setOrganizations(Array.isArray(orgs) ? orgs : []);
    } catch {
      console.error('Failed to load organizations');
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    if (!sessionLoaded) return;
    loadDevices();
  }, [sessionLoaded, loadDevices]);

  const cleanupScan = useCallback(() => {
    if (scanPollingRef.current) {
      clearInterval(scanPollingRef.current);
      scanPollingRef.current = null;
    }
    if (scanCountdownRef.current) {
      clearInterval(scanCountdownRef.current);
      scanCountdownRef.current = null;
    }
    setScanMode(false);
    setScanToken(null);
    setScanQrDataUrl(null);
    setScanExpiresAt(null);
    setScanTimeLeft('');
  }, []);

  const handleStartScan = async () => {
    try {
      const orgId = formOrgId === '__none__' ? undefined : formOrgId;
      const data = await clinicTellihealthApi.createScanToken(orgId);
      const token = data.token;
      const expiresAt = new Date(data.expiresAt);

      setScanToken(token);
      setScanExpiresAt(expiresAt);
      setScanMode(true);

      const scanUrl = `${window.location.origin}/scan/${token}`;
      const dataUrl = await QRCode.toDataURL(scanUrl, { width: 256, margin: 2 });
      setScanQrDataUrl(dataUrl);

      scanCountdownRef.current = setInterval(() => {
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 1000));
        const mins = Math.floor(remaining / 60);
        const secs = remaining % 60;
        setScanTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`);
        if (remaining <= 0) {
          toast.error('Scan session expired');
          cleanupScan();
        }
      }, 1000);

      scanPollingRef.current = setInterval(async () => {
        try {
          const result = await clinicTellihealthApi.getScanTokenResult(token);
          if (result.status === 'scanned') {
            setFormSerial(result.serialNumber || '');
            // PHA-1103: validate the scanned id against the selected manufacturer.
            if (result.imei && !isValidDeviceId(result.imei, formManufacturer)) {
              toast.error(deviceIdMessage(formManufacturer));
              cleanupScan();
            } else {
              setFormDeviceId(result.imei || '');
              toast.success('Device info received from phone scan!');
              cleanupScan();
            }
          } else if (result.status === 'expired') {
            toast.error('Scan session expired');
            cleanupScan();
          }
        } catch {
          // Silently retry on next interval
        }
      }, 3000);
    } catch {
      toast.error('Failed to start scan session');
    }
  };

  useEffect(() => {
    if (!dialogOpen) {
      cleanupScan();
    }
  }, [dialogOpen, cleanupScan]);

  useEffect(() => {
    return () => {
      if (scanPollingRef.current) clearInterval(scanPollingRef.current);
      if (scanCountdownRef.current) clearInterval(scanCountdownRef.current);
    };
  }, []);

  const handleRegister = async () => {
    setRegisterError(null);
    setShowErrorAlert(false);
    if (!formDeviceId.trim()) {
      setRegisterError(`${deviceIdLabel(formManufacturer)} is required`);
      return;
    }
    // PHA-1103: validate by manufacturer — SmartMeter glucose meters use 7-digit
    // ids that isValidImei rejects on length in every environment.
    if (!isValidDeviceId(formDeviceId.trim(), formManufacturer)) {
      setRegisterError(deviceIdMessage(formManufacturer));
      setShowErrorAlert(true);
      return;
    }
    setSubmitting(true);
    try {
      const result = await clinicTellihealthApi.registerDevice(
        formDeviceId.trim(),
        formOrgId === '__none__' ? undefined : formOrgId,
        formSerial.trim() || undefined,
        formDeviceType.trim() || undefined,
        formManufacturer.trim() || undefined
      );
      toast.success(`Device ${formDeviceId} registered`);
      // PHA-708: warn immediately if this IMEI has never transmitted any data.
      const stats = result?.readingStats;
      if (stats && stats.count === 0) {
        toast(`No readings on file yet for IMEI ${formDeviceId}. If this is a replacement, verify the number matches the unit; for a new device, take a test reading and confirm it appears.`, { icon: '⚠️', duration: 8000 });
      } else if (stats && stats.count > 0) {
        toast.success(`${stats.count} reading(s) already on file for this device.`);
      }
      setDialogOpen(false);
      setFormDeviceId('');
      setFormOrgId('__none__');
      setFormSerial('');
      setFormDeviceType('');
      setFormManufacturer('Telli-Health');
      loadDevices();
    } catch (err: any) {
      const body = err?.errorData || {};
      const errorMsg = body.detail
        ? `${body.error || 'Failed to register device'}: ${body.detail}`
        : body.error || 'Failed to register device';
      setRegisterError(errorMsg);
      setShowErrorAlert(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (device: Device) => {
    try {
      await clinicTellihealthApi.updateDeviceStatus(device.device_id, !device.active);
      toast.success(`Device ${device.device_id} ${!device.active ? 'activated' : 'deactivated'}`);
      loadDevices();
    } catch {
      toast.error('Failed to update device status');
    }
  };

  const handleRefreshGen = async (deviceId: string) => {
    try {
      const data = await clinicTellihealthApi.getDeviceGen(deviceId);
      toast.success(`Device gen: ${data.genVer}`);
      loadDevices();
    } catch {
      toast.error('Failed to fetch device generation');
    }
  };

  const handleRemoveDevice = async () => {
    if (!removeTarget) return;
    try {
      await clinicTellihealthApi.removeDevice(removeTarget.device_id);
      toast.success(`Device ${removeTarget.device_id} removed`);
      setRemoveTarget(null);
      loadDevices();
    } catch (err: any) {
      const body = err?.errorData || {};
      toast.error(body.error || 'Failed to remove device');
    }
  };

  const handleOrgChange = async (deviceId: string, newOrgId: string) => {
    setChangingOrgDeviceId(deviceId);
    try {
      await clinicTellihealthApi.updateDeviceOrganization(deviceId, newOrgId);
      toast.success('Device organization updated');
      loadDevices();
    } catch (err: any) {
      const body = err?.errorData || {};
      toast.error(body.error || 'Failed to update device organization');
    } finally {
      setChangingOrgDeviceId(null);
    }
  };

  return (
    <ClinicLayout title="Devices" subtitle="Manage Tellihealth RPM devices for your clinic">
      <Flex justify="between" align="center" mb="4">
        <Flex gap="3" align="center">
          <Button variant="soft" size="2" onClick={loadDevices} disabled={loading}>
            <ReloadIcon /> Refresh
          </Button>
        </Flex>

        <Dialog.Root open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) { setRegisterError(null); setShowErrorAlert(false); }
        }}>
          <Dialog.Trigger>
            <Button><PlusIcon /> Register Device</Button>
          </Dialog.Trigger>
          <Dialog.Content style={{ maxWidth: 450 }}>
            <Dialog.Title>Register New Device</Dialog.Title>
            <Dialog.Description size="2" mb="4">
              Enter the device IMEI and details. Device generation will be fetched automatically and alerts will be disabled.
            </Dialog.Description>

            {scanMode && scanQrDataUrl ? (
              <Box mb="3" p="3" style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                <Text size="2" weight="medium" mb="2" as="div">
                  Scan this QR code with your phone
                </Text>
                <img src={scanQrDataUrl} alt="Scan QR code" style={{ width: 200, height: 200, margin: '8px auto' }} />
                <Text size="1" color="gray" as="div" mb="2">
                  Time remaining: {scanTimeLeft}
                </Text>
                <Button variant="soft" size="1" color="gray" onClick={cleanupScan}>
                  Cancel Scan
                </Button>
              </Box>
            ) : (
              <Box mb="3">
                <Button variant="outline" size="2" onClick={handleStartScan} style={{ width: '100%' }}>
                  <CameraIcon /> Scan with Phone
                </Button>
              </Box>
            )}

            <Flex direction="column" gap="3">
              {/* PHA-1103: manufacturer first — it decides how the id below is
                  validated and labelled. */}
              <label>
                <Text size="2" weight="bold" mb="1" as="div">Manufacturer</Text>
                <Select.Root value={formManufacturer} onValueChange={setFormManufacturer}>
                  <Select.Trigger placeholder="Select manufacturer" />
                  <Select.Content>
                    {DEVICE_MANUFACTURERS.map((m) => (
                      <Select.Item key={m} value={m}>{m}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </label>
              <label>
                <Text size="2" weight="bold" mb="1" as="div">{deviceIdLabel(formManufacturer)} *</Text>
                <TextField.Root>
                  <TextField.Input
                    value={formDeviceId}
                    onChange={(e) => setFormDeviceId(e.currentTarget.value)}
                    placeholder={formManufacturer === SMARTMETER_MANUFACTURER ? '1234567' : '864593054464085'}
                  />
                </TextField.Root>
                {formManufacturer === SMARTMETER_MANUFACTURER && (
                  <Text size="1" color="gray" as="div" mt="1">
                    SmartMeter joins readings on the device id, not the IMEI — 15 digits for a blood pressure cuff, 7 for a glucose meter.
                  </Text>
                )}
              </label>
              {organizations.length > 1 && (
                <label>
                  <Text size="2" weight="bold" mb="1" as="div">Clinic</Text>
                  <Select.Root value={formOrgId} onValueChange={setFormOrgId}>
                    <Select.Trigger placeholder="Select clinic" />
                    <Select.Content>
                      <Select.Item value="__none__">None</Select.Item>
                      {organizations.map((org) => (
                        <Select.Item key={org.id} value={org.id}>{org.name}</Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </label>
              )}
              <label>
                <Text size="2" weight="bold" mb="1" as="div">Serial Number</Text>
                <TextField.Root>
                  <TextField.Input
                    value={formSerial}
                    onChange={(e) => setFormSerial(e.currentTarget.value)}
                    placeholder="From Telli purchase email"
                  />
                </TextField.Root>
              </label>
              <label>
                <Text size="2" weight="bold" mb="1" as="div">Device Type</Text>
                <TextField.Root>
                  <TextField.Input
                    value={formDeviceType}
                    onChange={(e) => setFormDeviceType(e.currentTarget.value)}
                    placeholder="e.g. Blood Pressure Cuff"
                  />
                </TextField.Root>
              </label>
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">Cancel</Button>
              </Dialog.Close>
              <Button onClick={handleRegister} disabled={submitting}>
                {submitting ? 'Registering...' : 'Register'}
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </Flex>

      <Table.Root variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>IMEI</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Clinic</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Serial</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Device Type</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Manufacturer</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Gen</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Linked Patient</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {loading && (
            <Table.Row>
              <Table.Cell colSpan={9}><Text size="2" color="gray">Loading...</Text></Table.Cell>
            </Table.Row>
          )}
          {!loading && devices.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={9}><Text size="2" color="gray">No devices registered</Text></Table.Cell>
            </Table.Row>
          )}
          {devices.map((device) => (
            <Table.Row key={device.device_id}>
              <Table.Cell>
                <Text size="2" style={{ fontFamily: 'monospace' }}>{device.device_id}</Text>
              </Table.Cell>
              <Table.Cell>
                {device.patient_id ? (
                  <Text size="2">{device.organization_name || '-'}</Text>
                ) : (
                  organizations.length > 1 ? (
                    <Select.Root
                      value={device.organization_id ?? undefined}
                      onValueChange={(val) => handleOrgChange(device.device_id, val)}
                      disabled={changingOrgDeviceId === device.device_id}
                    >
                      <Select.Trigger variant="ghost" />
                      <Select.Content>
                        {organizations.map((org) => (
                          <Select.Item key={org.id} value={org.id}>{org.name}</Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  ) : (
                    <Text size="2">{device.organization_name || '-'}</Text>
                  )
                )}
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{device.serial_number || '-'}</Text>
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{device.device_type || '\u2014'}</Text>
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{device.manufacturer || '\u2014'}</Text>
              </Table.Cell>
              <Table.Cell>
                <Flex align="center" gap="1">
                  <Text size="2">{device.gen_ver || '-'}</Text>
                  <Button variant="ghost" size="1" onClick={() => handleRefreshGen(device.device_id)} title="Refresh from API">
                    <ReloadIcon />
                  </Button>
                </Flex>
              </Table.Cell>
              <Table.Cell>
                <Badge color={device.active ? 'green' : 'red'} variant="soft">
                  {device.active ? 'Active' : 'Inactive'}
                </Badge>
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{device.patient_name || device.patient_id || '-'}</Text>
              </Table.Cell>
              <Table.Cell>
                <Flex gap="2">
                  <Button
                    variant="soft"
                    size="1"
                    color={device.active ? 'red' : 'green'}
                    onClick={() => handleToggleActive(device)}
                  >
                    {device.active ? 'Deactivate' : 'Activate'}
                  </Button>
                  {!device.patient_id && (
                    <Button variant="soft" size="1" color="red" onClick={() => setRemoveTarget(device)}>
                      <TrashIcon /> Remove
                    </Button>
                  )}
                </Flex>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>

      <AlertDialog.Root open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialog.Content style={{ maxWidth: 460 }}>
          <AlertDialog.Title>Remove Device</AlertDialog.Title>
          <AlertDialog.Description size="2" color="gray">
            Remove device {removeTarget?.device_id}? This action cannot be undone.
          </AlertDialog.Description>
          <Flex justify="end" gap="2" mt="4">
            <AlertDialog.Cancel>
              <Button variant="soft">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="solid" color="red" onClick={handleRemoveDevice}>
                Remove
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <AlertDialog.Root open={showErrorAlert} onOpenChange={(open) => { if (!open) setShowErrorAlert(false); }}>
        <AlertDialog.Content style={{ maxWidth: 420 }}>
          <AlertDialog.Title>Registration Failed</AlertDialog.Title>
          <AlertDialog.Description size="2">
            {registerError}
          </AlertDialog.Description>
          <Flex justify="end" mt="4">
            <AlertDialog.Action>
              <Button variant="solid" color="red">OK</Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </ClinicLayout>
  );
};

export default ClinicDevicesPage;
